import { Header } from '@/global/components/Header'
import { ResultPage, SimulatorContainer } from '@/modules/simulator/components'
import {
  SimulatorContext,
  SimulatorDispatchContext
} from '@/modules/simulator/contexts'
import { useContext, useEffect, useMemo, useState } from 'react'
import { PEOPLE_1_TO_5 } from '@/modules/simulator/constants/company-size'
import { CompanyDataForm } from '@/modules/simulator/components/Forms/CompanyDataForm'
import { MECompanyForm } from '@/modules/simulator/components/Forms/MECompanyForm'
import { EmployeesNumber } from '@/modules/simulator/components/Forms'
import { useInitSimulation } from '@/modules/simulator/hooks/useInitSimulation'
import { useSearchParams } from 'next/navigation'
import { isReaccessingMESimulation } from '@/modules/simulator/functions'
import { Loader } from '@/global/components/Loader'
import { motion, AnimatePresence } from 'framer-motion'
import { useGetUnifiedSimulatorEndpointFlag } from '@/modules/simulator/hooks/useGetUnifiedSimulatorEndpointFlag'

const Simulator = () => {
  const [showResult, setShowResult] = useState<boolean | undefined>(undefined)

  const searchParams = useSearchParams()

  const { simulation, step } = useContext(SimulatorContext)
  const { simulationDispatch } = useContext(SimulatorDispatchContext)
  const {
    isPending: isInitSimulationPending,
    isSuccess: isInitSimulationSuccess,
    mutate: initSimulation
  } = useInitSimulation()

  const {
    value: isUnifiedSimulatorEndpointFlag,
    isLoading: isUnifiedSimulatorEndpointFlagLoading
  } = useGetUnifiedSimulatorEndpointFlag()

  useEffect(() => {
    if (isReaccessingMESimulation(searchParams)) {
      simulationDispatch({
        type: 'SET_IS_REACCESSING_ME_SIMULATION',
        payload: true
      })
      setShowResult(true)
      return
    }

    return setShowResult(false)
  }, [])

  useEffect(() => {
    if (isReaccessingMESimulation(searchParams)) return

    if (
      !isUnifiedSimulatorEndpointFlagLoading &&
      !isUnifiedSimulatorEndpointFlag &&
      !simulation.id
    ) {
      initSimulation()
    }
    // Should depends only on the feature flag and the initialization status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUnifiedSimulatorEndpointFlagLoading,
    isUnifiedSimulatorEndpointFlag,
    simulation.id
  ])

  const onFinish = () => {
    setShowResult(true)
  }

  const employeesNumber = simulation.answers.companyEmployees
  const isMicroCompany = employeesNumber === PEOPLE_1_TO_5

  const isFirstStep = step.currentStep === 1
  const isSecondStep = step.currentStep === 2

  const showEmployeesNumberForm = isFirstStep
  const showMicroCompanyDataForm = isSecondStep && isMicroCompany
  const showCompanyDataForm = isSecondStep && !isMicroCompany

  const animationKey = `${step.currentStep}${showResult ? '-result' : ''}`

  const canInitSimulator = useMemo(() => {
    if (isUnifiedSimulatorEndpointFlagLoading) return false
    if (isUnifiedSimulatorEndpointFlag) return true
    return isInitSimulationSuccess
  }, [
    isUnifiedSimulatorEndpointFlagLoading,
    isUnifiedSimulatorEndpointFlag,
    isInitSimulationSuccess
  ])

  return (
    <div data-testid='simulator'>
      <header className='sticky top-0 z-50'>
        <Header />
      </header>

      <SimulatorContainer>
        {isInitSimulationPending ||
        isUnifiedSimulatorEndpointFlagLoading ||
        showResult === undefined ? (
          <Loader />
        ) : null}

        <AnimatePresence
          mode='wait'
          initial={false}
        >
          <motion.div
            key={animationKey}
            initial={{ y: -100, opacity: 0 }}
            exit={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 40 }}
            className='w-full h-full'
          >
            {canInitSimulator && showResult === false ? (
              <>
                {showEmployeesNumberForm ? <EmployeesNumber /> : null}
                {showMicroCompanyDataForm ? (
                  <MECompanyForm onFinish={onFinish} />
                ) : null}
                {showCompanyDataForm ? (
                  <CompanyDataForm onFinish={onFinish} />
                ) : null}
              </>
            ) : null}

            {showResult === true ? <ResultPage /> : null}
          </motion.div>
        </AnimatePresence>
      </SimulatorContainer>
    </div>
  )
}

export { Simulator }
